/**
 * "Reassign by year…" on a calibre's Revisions card — the pure planning half.
 *
 * Refs linked to a calibre that later joined a revision line stay on it even
 * when their year says another revision (ETA 7750 refs from 1990 → 7750-1).
 * This proposes, per ref, the revision the SHARED resolver picks
 * (resolveRevisionInLine — the bulk importer's rules: ref year → brand founded
 * when ≥ 2000 → current). Nothing is written here and nothing is pre-accepted:
 * every move is the curator's tick (James, 2026-11-03: opt-in only, no
 * provenance field). Refs with a curator signal are badged and never ticked
 * by "Accept all high-confidence".
 */
import {
  groupRefsByBrandAka,
  refYearOf,
  resolveRevisionInLine,
  type LineageMemberFields,
  type RevisionResolution,
} from './calibreLineageLogic';

export interface ReassignRef {
  brandId: string;
  refId: string;
  /** The reference document (only the fields read here are needed). */
  ref: Record<string, unknown>;
}

export interface ReassignRow<T extends LineageMemberFields> extends ReassignRef {
  suggestion: RevisionResolution<T>;
  /** Why the curator may have chosen this calibre on purpose, or null. */
  curatorSignal: string | null;
}

export const rowKey = (r: { brandId: string; refId: string }) => `${r.brandId}/${r.refId}`;

const hasOverrides = (v: unknown) => !!v && typeof v === 'object' && Object.keys(v as object).length > 0;

/**
 * One row per ref whose resolved revision differs from the calibre it is on.
 * `members` is the whole line, the calibre itself included.
 */
export function planReassignByYear<T extends LineageMemberFields>(input: {
  calibre: { id: string; aliasDetails?: ReadonlyArray<{ name: string; brandId?: string | null }> };
  members: readonly T[];
  refs: readonly ReassignRef[];
  foundedByBrand: ReadonlyMap<string, number | undefined>;
}): ReassignRow<T>[] {
  const { calibre, members, refs, foundedByBrand } = input;
  const akaRefs = new Set(
    groupRefsByBrandAka(
      (calibre.aliasDetails ?? []).map((a) => ({ name: a.name, brandId: a.brandId ?? undefined })),
      refs.map((r) => ({
        brandId: r.brandId,
        refId: r.refId,
        isVariant: typeof r.ref.parentReferenceId === 'string' && r.ref.parentReferenceId !== '',
        calibre: String((r.ref.calibreCache as { canonicalName?: unknown } | undefined)?.canonicalName ?? r.ref.calibre ?? ''),
      })),
    ).flatMap((g) => g.refs.map(rowKey)),
  );
  const rows: ReassignRow<T>[] = [];
  for (const r of refs) {
    const suggestion = resolveRevisionInLine({
      members,
      refYear: refYearOf(r.ref),
      brandFounded: foundedByBrand.get(r.brandId),
    });
    if (!suggestion || suggestion.member.id === calibre.id) continue;
    const curatorSignal = hasOverrides(r.ref.calibreOverrides)
      ? 'Has calibre overrides — the curator tuned this link'
      : akaRefs.has(rowKey(r))
        ? 'Came in under a brand-scoped AKA of this calibre'
        : null;
    rows.push({ ...r, suggestion, curatorSignal });
  }
  return rows.sort(
    (a, b) =>
      CONF_ORDER[b.suggestion.confidence] - CONF_ORDER[a.suggestion.confidence] ||
      a.brandId.localeCompare(b.brandId) ||
      String(a.ref.reference ?? a.refId).localeCompare(String(b.ref.reference ?? b.refId)),
  );
}

const CONF_ORDER = { high: 2, medium: 1, low: 0 } as const;

/** What "Accept all high-confidence" ticks: high rows without a curator signal. */
export function highConfidenceKeys(rows: ReadonlyArray<ReassignRow<LineageMemberFields>>): Set<string> {
  return new Set(rows.filter((r) => r.suggestion.confidence === 'high' && !r.curatorSignal).map(rowKey));
}
